import { proxyCustomElement, HTMLElement, createEvent, h, Fragment, Host } from '@stencil/core/internal/client';
import { d as dateToDateString, a as addDays } from './index13.js';
import { f as forwardEvent } from './index3.js';
import { a as getValidityObject, g as getError, c as checkValidity, r as reportValidity, b as setCustomValidity, w as watchError, d as watchWarning, e as getHostAttributes, f as getFormFieldAttributes } from './index7.js';
import { g as getBoxAttributes } from './util.js';
import { s as stringifyValue, p as parseValue, c as checkDateOverlaps, a as checkDateBounds, r as rangeWithinMaxDays, g as getNewValue, b as subDays } from './util3.js';
import { d as defineCustomElement$a } from './box.js';
import { d as defineCustomElement$9 } from './button.js';
import { d as defineCustomElement$8 } from './date-input.js';
import { d as defineCustomElement$7 } from './date-input-calendar.js';
import { d as defineCustomElement$6 } from './form-field.js';
import { d as defineCustomElement$5 } from './icon.js';
import { d as defineCustomElement$4 } from './loader.js';
import { d as defineCustomElement$3 } from './menu-pane.js';
import { d as defineCustomElement$2 } from './tooltip.js';
import { d as defineCustomElement$1 } from './tooltip-label.js';

const dateRangeInputCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.box{width:100%}.to-label{display:block;flex:none;font-family:Open Sans;font-size:.875rem;font-weight:600;height:2rem;letter-spacing:-.0004em;line-height:1.25rem;line-height:2rem}";
const WuiDateRangeInputStyle0 = dateRangeInputCss;

const WuiDateRangeInput = /*@__PURE__*/ proxyCustomElement(class WuiDateRangeInput extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.validityChecked = createEvent(this, "validityChecked", 7);
        this.validityReported = createEvent(this, "validityReported", 7);
        this.validateInternal = () => {
            // Read-only does not need validation
            if (this.readOnly) {
                return;
            }
            let valueParsed;
            try {
                valueParsed = parseValue(this.value);
            }
            catch (err) {
                // ignore bad value
            }
            if (this.required && !(valueParsed?.[0] && valueParsed[1])) {
                return {
                    validationMessage: 'Please fill in this field.',
                    validity: getValidityObject({ customError: true, valueMissing: true, valid: false }),
                };
            }
            // Check if value is within ranges
            if (typeof valueParsed === 'undefined') {
                return;
            }
            const [startDate, endDate] = valueParsed;
            try {
                checkDateOverlaps(startDate, endDate);
            }
            catch (err) {
                if (err instanceof Error) {
                    return {
                        validationMessage: err.message,
                        validity: getValidityObject({ customError: true, valid: false }),
                    };
                }
            }
            try {
                checkDateBounds({ date: startDate, pos: 'start', minDate: this.minDate, maxDate: this.maxDate });
            }
            catch (err) {
                if (err instanceof Error) {
                    return {
                        validationMessage: err.message,
                        validity: getValidityObject({ customError: true, valid: false }),
                    };
                }
            }
            try {
                checkDateBounds({ date: endDate, pos: 'end', minDate: this.minDate, maxDate: this.maxDate });
            }
            catch (err) {
                if (err instanceof Error) {
                    return {
                        validationMessage: err.message,
                        validity: getValidityObject({ customError: true, valid: false }),
                    };
                }
            }
            if (!rangeWithinMaxDays({ start: startDate, end: endDate }, this.maxDays)) {
                return {
                    validationMessage: `Date range may not exceed ${this.maxDays} days.`,
                    validity: getValidityObject({ customError: true, valid: false }),
                };
            }
            return {
                validationMessage: '',
                validity: getValidityObject(),
            };
        };
        this.dirty = false;
        this.getError = getError(this);
        this._handleValueChange = (idx, date) => {
            if (typeof date === 'undefined') {
                return;
            }
            const newValue = getNewValue(idx, date, this.value);
            if (typeof newValue === 'undefined') {
                return;
            }
            this._setValue(newValue);
        };
        this.value = undefined;
        this.required = false;
        this.error = undefined;
        this.warning = undefined;
        this.validate = undefined;
        this.validity = getValidityObject();
        this.validationMessage = '';
        this.customValidation = undefined;
        this.checkValidity = checkValidity(this);
        this.reportValidity = reportValidity(this);
        this.setCustomValidity = setCustomValidity(this);
        this.showValidity = false;
        this.formField = undefined;
        this.name = '';
        this.disabled = false;
        this.readOnly = false;
        this.placeholder = 'Select a date range';
        this.minDate = undefined;
        this.maxDate = undefined;
        this.maxDays = undefined;
        this.dateInputStart = undefined;
        this.dateInputEnd = undefined;
        this.valueAsDateRange = undefined;
    }
    _watchError(...args) {
        watchError(this, ...args);
    }
    _watchWarning(...args) {
        watchWarning(this, ...args);
    }
    _setValue(value) {
        const newValue = value ? stringifyValue(value) : undefined;
        if (this.value === newValue) {
            // Don't emit same value
            return;
        }
        this.value = newValue;
        this.elm.dispatchEvent(new window.Event('input', { bubbles: true }));
        this.dirty = true;
        this.checkValidity();
    }
    renderInner() {
        let valueParsed;
        try {
            valueParsed = parseValue(this.value);
        }
        catch (err) {
            // ignore bad values
        }
        return (h(Fragment, null, h("wui-box", { orientation: "horizontal", gutter: "sm", align: "end", class: "box", ...getBoxAttributes(this) }, h("wui-date-input", { value: valueParsed?.[0] ?? undefined, rangeEndDate: valueParsed?.[1] ?? undefined, onInput: (e) => {
                const target = e.currentTarget;
                if (!target) {
                    return;
                }
                const value = target.value;
                this._handleValueChange(0, value);
            }, onBlur: (e) => {
                forwardEvent(e, this.elm);
            }, onFocus: (e) => {
                forwardEvent(e, this.elm);
            }, error: this.showValidity ? !!this.validationMessage : false, disabled: this.disabled || this.readOnly, min: (() => {
                const [, end] = valueParsed ?? [];
                if (end && this.maxDays) {
                    return dateToDateString(subDays(end, this.maxDays - 1));
                }
                return this.minDate;
            })(), max: this.maxDate, required: this.required, formField: { label: 'Start date', ...this.dateInputStart?.formField } }), h("b", { class: "to-label" }, "to"), h("wui-date-input", { value: valueParsed?.[1] ?? undefined, rangeStartDate: valueParsed?.[0] ?? undefined, onInput: (e) => {
                const target = e.currentTarget;
                if (!target) {
                    return;
                }
                const value = target.value;
                this._handleValueChange(1, value);
            }, onBlur: (e) => {
                forwardEvent(e, this.elm);
            }, onFocus: (e) => {
                forwardEvent(e, this.elm);
            }, error: this.showValidity ? !!this.validationMessage : false, disabled: this.disabled || this.readOnly, min: this.minDate, max: (() => {
                const [start] = valueParsed ?? [];
                if (start && this.maxDays) {
                    return dateToDateString(addDays(start, this.maxDays - 1));
                }
                return this.maxDate;
            })(), required: this.required, formField: { label: 'End date', ...this.dateInputEnd?.formField } }))));
    }
    render() {
        return (h(Host, { key: 'ef57bdbae5b30054c55d8cb90d10f9aa606ecf2c', ...getHostAttributes(this) }, h("wui-form-field", { key: '8486707cb53f2367c0a2b2835db4ff34efc59012', ...getFormFieldAttributes(this) }, this.renderInner())));
    }
    get elm() { return this; }
    static get watchers() { return {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }; }
    static get style() { return WuiDateRangeInputStyle0; }
}, [1, "wui-date-range-input", {
        "value": [1025],
        "required": [8],
        "error": [8],
        "warning": [8],
        "validate": [16],
        "validity": [1040],
        "validationMessage": [1025, "validation-message"],
        "checkValidity": [16],
        "reportValidity": [16],
        "setCustomValidity": [16],
        "formField": [16],
        "name": [1],
        "disabled": [516],
        "readOnly": [516, "read-only"],
        "placeholder": [1],
        "minDate": [1, "min-date"],
        "maxDate": [1, "max-date"],
        "maxDays": [2, "max-days"],
        "dateInputStart": [16],
        "dateInputEnd": [16],
        "valueAsDateRange": [1040],
        "customValidation": [32],
        "showValidity": [32]
    }, undefined, {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }]);
WuiDateRangeInput.stringifyValue = stringifyValue;
WuiDateRangeInput.parseValue = parseValue;
Object.defineProperties(WuiDateRangeInput.prototype, {
    valueAsDateRange: {
        configurable: true,
        get() {
            return parseValue(this.value);
        },
        set(newValue) {
            this.value = newValue ? stringifyValue(newValue) : undefined;
            this.dirty = true;
            this.checkValidity();
        },
    },
});
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-date-range-input", "wui-box", "wui-button", "wui-date-input", "wui-date-input-calendar", "wui-form-field", "wui-icon", "wui-loader", "wui-menu-pane", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-date-range-input":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiDateRangeInput);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "wui-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-date-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-date-input-calendar":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-menu-pane":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { WuiDateRangeInput as W, defineCustomElement as d };

//# sourceMappingURL=date-range-input.js.map